import dayjs from "dayjs";
import * as React from "react";

interface CommentTimeProps {
  /** ISO 8601 timestamp string */
  timestamp: string;

  /** Optional className for styling */
  className?: string;
}

/**
 * Displays a comment timestamp in a human-readable format.
 *
 * - For timestamps within the last 24 hours: shows relative time (e.g., "5 minutes ago", "2 hours ago")
 * - For timestamps older than 24 hours: shows absolute time (e.g., "Jan 1, 2021 at 12:00 PM")
 */
export function CommentTime({ timestamp, className }: CommentTimeProps) {
  const displayTime = React.useMemo(() => {
    const date = new Date(timestamp);
    const now = dayjs();
    const commentDate = dayjs(date);
    const hoursDiff = now.diff(commentDate, "hours");

    // If less than 24 hours ago, show relative time
    if (hoursDiff < 24) {
      return dayjs.duration(commentDate.diff(now)).humanize(true);
    }

    // Otherwise, show absolute time
    // Format: "Jan 1, 2021 at 12:00 PM"
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, [timestamp]);

  const tooltipTime = React.useMemo(() => {
    return new Date(timestamp).toLocaleString();
  }, [timestamp]);

  return (
    <time dateTime={timestamp} className={className} title={tooltipTime}>
      {displayTime}
    </time>
  );
}
